import { type KeyValueStorageInterface } from "aws-amplify/utils";

import { LoggerService } from "../services";

// Best-effort cleanup when the object is garbage collected.
const registry = new FinalizationRegistry<() => void>((dispose) => {
  LoggerService.warn("IndexDbStorage is being garbage collected");
  dispose();
});

/**
 * An indexedDB storage implementation for Amplify's custom auth storage.
 * Amplify uses a localStorage implementation by default,
 * but localStorage is not available in web workers.
 * By using indexedDB, we can use Amplify to retrieve bearer tokens in web workers.
 *
 * Ref: https://docs.amplify.aws/react/build-a-backend/auth/manage-user-session/#custom-storage
 */
export class IndexDbStorage implements KeyValueStorageInterface {
  public static DB_NAME = "auth";
  public static VERSION = 1;
  public static STORE_NAME = "cognito-user-pools-token-storage";
  private static readonly DB_LOCK_NAME = "indexdb-storage-open-lock";

  #db: IDBDatabase;

  static build(): Promise<IndexDbStorage> {
    return IndexDbStorage.openDb().then((db) => new IndexDbStorage(db));
  }

  private static openDb(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = globalThis.indexedDB.open(
        IndexDbStorage.DB_NAME,
        IndexDbStorage.VERSION,
      );

      request.onerror = () => {
        const msg = `Error opening browser storage to store authentication. 
          If you were prompted to allow this site access to 'IndexedDB', please allow it. 
          Otherwise, please refresh the page and try again.`;
        globalThis.indexedDB.deleteDatabase(IndexDbStorage.DB_NAME);
        reject(new Error(msg, { cause: request.error }));
      };

      // Called when the database is created or the version is updated, before the onsuccess event is triggered.
      request.onupgradeneeded = () => {
        const db = request.result;
        if (db.objectStoreNames.contains(IndexDbStorage.STORE_NAME)) {
          db.deleteObjectStore(IndexDbStorage.STORE_NAME);
        }
        db.createObjectStore(IndexDbStorage.STORE_NAME);
      };

      request.onsuccess = () => {
        const db = request.result;
        db.onerror = (event) => {
          const msg = `Error accessing cached authentication. Please try refreshing the page and logging in again.`;
          reject(new Error(msg, { cause: event.target }));
        };

        db.onclose = () => {
          LoggerService.warn("IndexDbStorage is being closed");
        };

        resolve(db);
      };
    });
  }

  constructor(db: IDBDatabase) {
    this.#db = db;
    registry.register(this, () => this.close());
  }

  close(): void {
    this.#db.close();
  }

  setItem(key: string, value: string): Promise<void> {
    return this.withRetryConnection(() => {
      return new Promise((resolve, reject) => {
        const store = this.startTransaction("readwrite", {
          onerror: reject,
          oncomplete: () => resolve(),
        });
        store.put(value, key);
      });
    });
  }

  getItem(key: string): Promise<string | null> {
    return this.withRetryConnection(() => {
      return new Promise((resolve, reject) => {
        const store = this.startTransaction("readonly", {
          onerror: reject,
        });

        const request = store.get(key) as IDBRequest<string>;
        request.onsuccess = () => {
          resolve(request.result);
        };
      });
    });
  }

  removeItem(key: string): Promise<void> {
    return this.withRetryConnection(() => {
      return new Promise((resolve, reject) => {
        const store = this.startTransaction("readwrite", {
          onerror: reject,
          oncomplete: () => resolve(),
        });
        store.delete(key);
      });
    });
  }

  clear(): Promise<void> {
    return this.withRetryConnection(() => {
      return new Promise((resolve, reject) => {
        const store = this.startTransaction("readwrite", {
          onerror: reject,
          oncomplete: () => resolve(),
        });
        store.clear();
      });
    });
  }

  /**
   * Checks if an error indicates that the IndexedDB connection is closed.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/IDBDatabase/transaction
   */
  private isConnectionClosedError(err: unknown): boolean {
    return err instanceof DOMException && err.name === "InvalidStateError";
  }

  private isConnectionClosed(): boolean {
    try {
      this.#db.transaction(IndexDbStorage.STORE_NAME, "readonly");
      return false;
    } catch (err) {
      return this.isConnectionClosedError(err);
    }
  }

  private startTransaction(
    mode: IDBTransactionMode,
    handlers: {
      onerror: (error: Error) => void;
      oncomplete?: () => void;
    },
  ): IDBObjectStore {
    const transaction = this.#db.transaction(IndexDbStorage.STORE_NAME, mode);

    transaction.onerror = () => {
      handlers.onerror(
        new Error("Transaction failed", { cause: transaction.error }),
      );
    };

    // https://developer.mozilla.org/en-US/docs/Web/API/IDBTransaction/abort_event
    transaction.onabort = () => {
      handlers.onerror(
        new Error("Transaction aborted", {
          cause:
            transaction.error ||
            "Transaction was manually or unexpectedly aborted without an error",
        }),
      );
    };

    if (handlers.oncomplete) {
      transaction.oncomplete = handlers.oncomplete;
    }

    return transaction.objectStore(IndexDbStorage.STORE_NAME);
  }

  /**
   * Helper to retry with a new connection if the previous connection is closed.
   */
  private withRetryConnection<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      fn()
        .then(resolve)
        .catch((err) => {
          if (this.isConnectionClosedError(err)) {
            LoggerService.warn(
              "IndexDbStorage connection is closed, reopening",
            );

            // Use Web Locks API to ensure only one reopening happens at a time
            void navigator.locks.request(
              IndexDbStorage.DB_LOCK_NAME,
              async () => {
                try {
                  // Check if the connection is still closed before reopening
                  // A previous request might have already fixed it
                  if (this.isConnectionClosed()) {
                    const db = await IndexDbStorage.openDb();
                    this.#db = db;
                  }
                  resolve(fn());
                } catch (reopenErr) {
                  reject(
                    reopenErr instanceof Error
                      ? reopenErr
                      : new Error(String(reopenErr)),
                  );
                }
              },
            );
          } else {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        });
    });
  }
}
